/**
 * Cross-Tab Session Synchronization
 * Handles synchronization of authentication state across multiple browser tabs
 */

import { ref, onMounted, onUnmounted } from 'vue';
import { validateSessionWithRefresh } from '@/store/helpers/authenticate/tokens';
import { useUserStore } from '@/store/user.store';
import { useApplicationStore } from '@/store/application.store';
import { router } from '@/router';

export interface CrossTabSyncOptions {
  enabled?: boolean;
  syncInterval?: number;
  signalTimeout?: number;
}

export interface TabSyncEvent {
  type: 'login' | 'logout' | 'token_refresh' | 'auth_state';
  timestamp: number;
  tabId: string;
  data?: any;
}

export function useCrossTabSync(options: CrossTabSyncOptions = {}) {
  const enabled = ref(options.enabled ?? true);
  const syncInterval = options.syncInterval ?? 30000; // 30 seconds
  const signalTimeout = options.signalTimeout ?? 5000; // 5 seconds
  
  let syncIntervalId: NodeJS.Timeout | null = null;
  let tabId = '';

  /**
   * Generate unique tab identifier
   */
  const generateTabId = (): string => {
    if (!tabId) {
      tabId = Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('cross_tab_sync_id', tabId);
    }
    return tabId;
  };

  /**
   * Handle storage events from other tabs
   */
  const handleStorageChange = async (event: StorageEvent) => {
    if (!enabled.value || !event.key) return;

    const { key, newValue, oldValue } = event;
    
    console.log('[CrossTabSync] Storage change detected:', { key, newValue, oldValue });

    try {
      // Handle authentication token changes
      if (key === 'descope_session') {
        await handleSessionTokenChange(newValue, oldValue);
      }
      // Handle explicit sync signals
      else if (key === 'cross_tab_sync_signal') {
        await handleSyncSignal(newValue);
      }
      // Handle authentication state changes
      else if (key === 'cross_tab_auth_state') {
        await handleAuthStateChange(newValue);
      }
    } catch (error) {
      console.error('[CrossTabSync] Error handling storage change:', error);
    }
  };

  /**
   * Handle session token changes from other tabs
   */
  const handleSessionTokenChange = async (newValue: string | null, oldValue: string | null) => {
    const userStore = useUserStore();
    
    if (!newValue && oldValue && userStore.isAuthenticated) {
      // Token was removed - likely logout
      console.log('[CrossTabSync] Session token removed in another tab');
      await handleCrossTabLogout();
    }
    else if (newValue && !oldValue && !userStore.isAuthenticated) {
      // Token was added - likely login
      console.log('[CrossTabSync] Session token added in another tab');
      await handleCrossTabLogin();
    }
    else if (newValue && oldValue && newValue !== oldValue) {
      // Token was refreshed
      console.log('[CrossTabSync] Session token refreshed in another tab');
      await validateCrossTabTokenRefresh();
    }
  };

  /**
   * Handle sync signals from other tabs
   */
  const handleSyncSignal = async (signalData: string | null) => {
    if (!signalData) return;

    try {
      const signal: TabSyncEvent = JSON.parse(signalData);
      
      // Ignore our own signals
      if (signal.tabId === tabId) return;
      
      // Only process recent signals
      if (Date.now() - signal.timestamp > signalTimeout) return;

      console.log('[CrossTabSync] Received signal:', signal);

      switch (signal.type) {
        case 'login':
          await handleCrossTabLogin();
          break;
        case 'logout':
          await handleCrossTabLogout();
          break;
        case 'token_refresh':
          await validateCrossTabTokenRefresh();
          break;
        default:
          console.warn('[CrossTabSync] Unknown signal type:', signal.type);
      }

      // Clean up the signal after processing
      setTimeout(() => {
        if (localStorage.getItem('cross_tab_sync_signal') === signalData) {
          localStorage.removeItem('cross_tab_sync_signal');
        }
      }, 1000);

    } catch (error) {
      console.error('[CrossTabSync] Error parsing sync signal:', error);
    }
  };

  /**
   * Handle authentication state changes
   */
  const handleAuthStateChange = async (stateData: string | null) => {
    if (!stateData) return;

    try {
      const authState = JSON.parse(stateData);
      const userStore = useUserStore();
      
      if (authState.isAuthenticated !== userStore.isAuthenticated) {
        console.log('[CrossTabSync] Auth state mismatch detected:', authState);
        
        if (authState.isAuthenticated && !userStore.isAuthenticated) {
          await handleCrossTabLogin();
        } else if (!authState.isAuthenticated && userStore.isAuthenticated) {
          await handleCrossTabLogout();
        }
      }
    } catch (error) {
      console.error('[CrossTabSync] Error handling auth state change:', error);
    }
  };

  /**
   * Handle cross-tab logout
   */
  const handleCrossTabLogout = async () => {
    try {
      console.log('[CrossTabSync] Synchronizing logout from another tab');
      
      const userStore = useUserStore();
      const applicationStore = useApplicationStore();
      
      // Clear authentication state
      userStore.setState({ key: 'isAuthenticated', value: false });
      userStore.setState({ key: 'isExplicitLogout', value: true });
      userStore.reset();
      
      // Show notification
      applicationStore.showNotification({
        title: 'Session Ended',
        message: 'You have been logged out from another browser tab.',
        theme: 'primary',
        autoClose: true,
        timeout: 5000,
        isOpen: true
      });

      // Redirect to login if on protected route
      const currentRoute = router.currentRoute.value;
      if (currentRoute.meta?.requiresAuth) {
        try {
          const { navigateWithLoadingState } = await import('@/utils/navigation');
          const { NAME_LOGIN } = await import('@/router/routes/constants');
          await navigateWithLoadingState({ name: NAME_LOGIN });
        } catch {
          await router.push('/login');
        }
      }
      
    } catch (error) {
      console.error('[CrossTabSync] Error handling cross-tab logout:', error);
    }
  };

  /**
   * Handle cross-tab login
   */
  const handleCrossTabLogin = async () => {
    try {
      console.log('[CrossTabSync] Synchronizing login from another tab');
      
      // Validate the session first
      const sessionValidation = await validateSessionWithRefresh();
      
      if (sessionValidation.valid) {
        const userStore = useUserStore();
        const applicationStore = useApplicationStore();
        
        // Update authentication state
        userStore.setState({ key: 'isAuthenticated', value: true });
        userStore.setState({ key: 'isExplicitLogout', value: false });
        
        // Show notification
        applicationStore.showNotification({
          title: 'Session Synchronized',
          message: 'Authentication synchronized from another browser tab.',
          theme: 'success',
          autoClose: true,
          timeout: 5000,
          isOpen: true
        });
        
        console.log('[CrossTabSync] Cross-tab login successful');
      } else {
        console.warn('[CrossTabSync] Session validation failed during cross-tab login');
      }
      
    } catch (error) {
      console.error('[CrossTabSync] Error handling cross-tab login:', error);
    }
  };

  /**
   * Validate token refresh from another tab
   */
  const validateCrossTabTokenRefresh = async () => {
    try {
      console.log('[CrossTabSync] Validating token refresh from another tab');
      
      const sessionValidation = await validateSessionWithRefresh();
      
      if (sessionValidation.valid) {
        console.log('[CrossTabSync] Token refresh validated successfully');
        
        // Refresh custom claims if needed
        const userStore = useUserStore();
        if (userStore.authUser) {
          await userStore.refreshCustomClaims();
        }
      } else {
        console.warn('[CrossTabSync] Token refresh validation failed');
      }
      
    } catch (error) {
      console.error('[CrossTabSync] Error validating token refresh:', error);
    }
  };

  /**
   * Perform periodic synchronization check
   */
  const performPeriodicSync = async () => {
    if (!enabled.value) return;

    try {
      const userStore = useUserStore();
      const hasToken = !!localStorage.getItem('descope_session');
      
      // Check for inconsistencies
      if (userStore.isAuthenticated && !hasToken) {
        console.warn('[CrossTabSync] Inconsistency: authenticated but no token');
        await handleCrossTabLogout();
      }
      else if (!userStore.isAuthenticated && hasToken) {
        console.log('[CrossTabSync] Inconsistency: token exists but not authenticated');
        await handleCrossTabLogin();
      }

      // Update sync timestamp
      localStorage.setItem('cross_tab_last_sync', Date.now().toString());

    } catch (error) {
      console.error('[CrossTabSync] Error during periodic sync:', error);
    }
  };

  /**
   * Send signal to other tabs
   */
  const sendSignalToOtherTabs = (type: TabSyncEvent['type'], data?: any) => {
    if (!enabled.value) return;

    const signal: TabSyncEvent = {
      type,
      timestamp: Date.now(),
      tabId: generateTabId(),
      data
    };
    
    localStorage.setItem('cross_tab_sync_signal', JSON.stringify(signal));
    
    // Also update auth state for redundancy
    const userStore = useUserStore();
    localStorage.setItem('cross_tab_auth_state', JSON.stringify({
      isAuthenticated: userStore.isAuthenticated,
      timestamp: Date.now()
    }));
    
    console.log('[CrossTabSync] Signal sent:', signal);
  };

  /**
   * Signal login to other tabs
   */
  const signalLogin = () => {
    sendSignalToOtherTabs('login');
  };

  /**
   * Signal logout to other tabs
   */
  const signalLogout = () => {
    sendSignalToOtherTabs('logout');
  };

  /**
   * Signal token refresh to other tabs
   */
  const signalTokenRefresh = () => {
    sendSignalToOtherTabs('token_refresh');
  };

  /**
   * Handle tab unload
   */
  const handleTabUnload = () => {
    // Clean up tab-specific data
    sessionStorage.removeItem('cross_tab_sync_id');
  };

  /**
   * Initialize cross-tab sync
   */
  const initialize = () => {
    if (!enabled.value || typeof window === 'undefined') {
      return;
    }

    console.log('[CrossTabSync] Initializing cross-tab session synchronization');

    // Generate tab ID
    generateTabId();

    // Set up event listeners
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('beforeunload', handleTabUnload);

    // Start periodic sync
    syncIntervalId = setInterval(performPeriodicSync, syncInterval);

    // Initial sync check
    performPeriodicSync();
  };

  /**
   * Cleanup cross-tab sync
   */
  const cleanup = () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('beforeunload', handleTabUnload);
    }

    if (syncIntervalId) {
      clearInterval(syncIntervalId);
      syncIntervalId = null;
    }

    console.log('[CrossTabSync] Cleanup completed');
  };

  /**
   * Enable/disable sync
   */
  const setEnabled = (value: boolean) => {
    enabled.value = value;
    
    if (value) {
      initialize();
    } else {
      cleanup();
    }
  };

  // Auto-initialize on mount
  onMounted(() => {
    if (enabled.value) {
      initialize();
    }
  });

  // Cleanup on unmount
  onUnmounted(() => {
    cleanup();
  });

  return {
    enabled,
    initialize,
    cleanup,
    setEnabled,
    signalLogin,
    signalLogout,
    signalTokenRefresh,
    sendSignalToOtherTabs
  };
}
